import { StyleSheet, css } from 'aphrodite';
import { type CSSProperties, type FC, useState } from 'react';
import { toast } from 'sonner';
import { FormFieldType } from '../../../extViewmodels';
import { useDebounceValue } from '../../../hooks/useDebounceValue';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemRadioIndicator,
	ComboboxList,
	ComboboxStatus,
	ComboboxTrigger,
} from '../../../web/components-v2/Combobox';
import { THEME } from '../../../web/components-v2/Theme/theme';
import {
	type FocusedLeadAttribute,
	type LeadAttributeOption,
	useLeadAttributeOptions,
	useUpdateLeadAttribute,
} from '../data';
import { readableInk } from './colorContrast';

type LeadAttributePillProps = {
	companyId: string;
	attribute: FocusedLeadAttribute;
};

const SEARCH_DEBOUNCE_MS = 300;

function pillColors(hexColor: string | undefined, isUnknown: boolean): CSSProperties {
	if (hexColor) return { background: hexColor, color: readableInk(hexColor) };
	if (isUnknown) return { background: 'var(--attr-unknown-bg)', color: 'var(--attr-unknown-ink)' };
	return { background: 'var(--attr-bg)', color: 'var(--attr-ink)' };
}

const OptionLabel = ({ option }: { option: LeadAttributeOption }) =>
	option.hexColor ? (
		<span
			className={css(styles.optionSwatch)}
			style={{ background: option.hexColor, color: readableInk(option.hexColor) }}
		>
			{option.label}
		</span>
	) : (
		<span className={css(styles.optionText)}>{option.label}</span>
	);

export const LeadAttributePill: FC<LeadAttributePillProps> = ({ companyId, attribute }) => {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState('');
	const [pendingOption, setPendingOption] = useState<LeadAttributeOption | null>(null);
	const trimmedSearch = search.trim();
	const debouncedSearch = useDebounceValue(trimmedSearch, SEARCH_DEBOUNCE_MS);
	// An emptied box takes effect immediately instead of waiting out the debounce, so
	// reopening the popup can't re-query with the term left over from last time.
	const searchTerm = trimmedSearch === '' ? '' : debouncedSearch;
	const editable = attribute.fieldType === FormFieldType.Option;
	// Options load on demand — only once the dropdown opens.
	const { options, isFetching } = useLeadAttributeOptions(
		companyId,
		editable ? attribute.id : undefined,
		open,
		searchTerm
	);
	const updateAttribute = useUpdateLeadAttribute();

	const saving = updateAttribute.isPending && pendingOption !== null;
	const displayValue = saving ? pendingOption.label : attribute.value;
	const selectedId = saving ? pendingOption.id : attribute.valueId;
	const colors = pillColors(attribute.hexColor, !displayValue);

	if (!editable) {
		return (
			<span className={css(styles.pill)} style={colors} title={attribute.label}>
				<span className={css(styles.label)}>{attribute.label}:</span>
				<span>{displayValue || 'Unknown'}</span>
			</span>
		);
	}

	const handleOpenChange = (next: boolean) => {
		setOpen(next);
		if (!next) {
			setSearch('');
		}
	};

	const handleValueChange = (option: LeadAttributeOption | null) => {
		if (!option) {
			return;
		}
		handleOpenChange(false);
		setPendingOption(option);
		updateAttribute.mutate(
			{ companyId, fieldId: attribute.id, value: option.id },
			{
				onError: () => toast.error(`Couldn't update ${attribute.label}`),
				// Drop the optimistic label once the write settles — on success the
				// refetched lead carries the new value; on error we revert.
				onSettled: () => setPendingOption(null),
			}
		);
	};

	return (
		<Combobox
			items={options}
			value={displayValue ? { id: selectedId ?? '', label: displayValue, hexColor: attribute.hexColor } : null}
			inputValue={search}
			onInputValueChange={setSearch}
			open={open}
			onOpenChange={handleOpenChange}
			onValueChange={value => handleValueChange(value as LeadAttributeOption | null)}
			filter={null}
			// Fall back to the label only when the lead payload carried no option id —
			// two options can share a label, so the id wins whenever we have one.
			isItemEqualToValue={(item, value) => {
				const option = item as LeadAttributeOption;
				const selected = value as LeadAttributeOption;
				return selected.id ? option.id === selected.id : option.label === selected.label;
			}}
			itemToStringValue={item => (item as LeadAttributeOption).label}
			itemToStringLabel={item => (item as LeadAttributeOption).label}
		>
			<ComboboxTrigger
				render={({ className, ...props }) => (
					<button
						{...props}
						type='button'
						className={[className, css(styles.pill, styles.pillButton)].filter(Boolean).join(' ')}
						style={colors}
						disabled={updateAttribute.isPending}
						aria-label={`Edit ${attribute.label}`}
						title={`Edit ${attribute.label}`}
					>
						<span className={css(styles.label)}>{attribute.label}:</span>
						<span>{displayValue || 'Unknown'}</span>
						<span className={css(styles.caret)} aria-hidden='true'>
							▾
						</span>
					</button>
				)}
			/>
			<ComboboxContent styles={[styles.dropdown]}>
				<div className={css(styles.searchWrapper)}>
					<ComboboxInput placeholder={`Search ${attribute.label}…`} showTrigger={false} />
				</div>
				{/* base-ui asks that the live region stay mounted and only its children change. */}
				<ComboboxStatus styles={[styles.status]}>{isFetching ? 'Searching…' : null}</ComboboxStatus>
				<ComboboxEmpty>{isFetching ? null : 'No matching options'}</ComboboxEmpty>
				<ComboboxList styles={[styles.list]}>
					{(option: LeadAttributeOption) => (
						<ComboboxItem key={option.id} value={option}>
							<ComboboxItemRadioIndicator />
							<OptionLabel option={option} />
						</ComboboxItem>
					)}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
};

const styles = StyleSheet.create({
	pill: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 4,
		height: 24,
		paddingLeft: 10,
		paddingRight: 10,
		// Intentional fully-rounded pill shape (not the 8px theme default).
		borderRadius: 999,
		fontSize: 12,
		fontWeight: 500,
		border: 'none',
		whiteSpace: 'nowrap',
	},
	pillButton: {
		cursor: 'pointer',
		fontFamily: 'inherit',
		// The pill's background is the option's own color, so the ring keys off the
		// contrast-picked ink rather than a fixed palette color.
		':focus-visible': {
			outline: '2px solid currentColor',
			outlineOffset: 1,
		},
	},
	label: {
		opacity: 0.7,
	},
	caret: {
		fontSize: 9,
		marginLeft: 2,
	},
	dropdown: {
		width: 'min(max(var(--anchor-width), 260px), var(--available-width))',
		minWidth: 'min(max(var(--anchor-width), 260px), var(--available-width))',
	},
	searchWrapper: {
		padding: THEME.spacing.sm,
		paddingBottom: THEME.spacing.xs,
	},
	status: {
		':empty': {
			padding: 0,
		},
	},
	list: {
		maxHeight: 240,
		overflowY: 'auto',
	},
	optionSwatch: {
		display: 'inline-flex',
		alignItems: 'center',
		minWidth: 0,
		paddingLeft: THEME.spacing.sm,
		paddingRight: THEME.spacing.sm,
		borderRadius: 999,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	optionText: {
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});
